import { useStore } from "vuex";
import type { ShoppingCartItem } from "~/components/ecommerce/types/ShoppingCartItem";
import { ShoppingCartItemEnum } from "~/components/ecommerce/types/ShoppingCartItemEnum";
import { mapTradeInItem } from "~/helpers/ecommerce/shoppingCartItemMapper";
import type { TradeInInfo } from "~/components/hardware-concepts/types/TradeInInfo";
import deviceReturns from "~/integrations/device-returns";
import type { TradeInSteps } from "~/components/hardware-concepts/types/TradeInSteps";
import { formatPrice } from "~/helpers/formatting/price";
import addQueryParams from "~/helpers/hardware-and-insurance/addQueryParams";
import { TradeInOption } from "~/components/hardware-concepts/types/TradeInOption";

export const useTradeIn = (shoppingCart?: any) => {
	const store = useStore();
	const route = useRoute();
	const { nextAction, prevAction } = useFlow({
		disableAutoInitGraph: !["innbytte", "checkout", "swap"].some((path) => route.path.includes(path)),
	});

	// -- state --
	const loading = ref(false);
	const deviceOrder = ref<any>(null);

	// -- core getters --
	const items = computed<ShoppingCartItem<TradeInInfo>[]>(() =>
		shoppingCart?.length ? shoppingCart : store.getters["shoppingCart/items"],
	);

	const tradeInItem = computed<ShoppingCartItem<TradeInInfo> | undefined>(() =>
		items.value?.find((item: ShoppingCartItem<TradeInInfo>) => item.type === ShoppingCartItemEnum.TRADE_IN),
	);
	const meta = computed<TradeInInfo>(() => tradeInItem?.value?.metaData);
	const currentTerminal = computed(() => store.getters["checkout/currentTerminal"]);
	const returnPath = computed<string | undefined>(() =>
		Array.isArray(route.query.path) ? route.query.path[0] : route.query.path,
	);

	// -- flattened meta --
	const phoneData = computed(() => meta.value?.phoneData || {});
	const gradingAnswers = computed(() => meta.value?.gradingAnswers || []);
	const gradingData = computed(() => meta.value?.gradingData || {});
	const insuranceClaimData = computed(() => meta.value?.insuranceClaimData || {});
	const steps = computed<TradeInSteps>(() => meta.value?.steps || {});
	const selectedTradeInOption = computed(() => meta.value?.tradeInOption);
	const newPhoneData = computed(() => meta.value?.newPhoneData);
	const isSwap = computed(() => !!meta.value?.hasSwap);
	const hasDeviceOrder = computed(() => !!deviceOrder.value?.orderId);
	const isOffset = computed(() => !!currentTerminal.value?.metadata?.downPayment);

	// -- derived values --
	const bonusInfoObject = computed(() => {
		const imei = Array.isArray(route.query.imei) ? route.query.imei[0] : route.query.imei;
		const gtin = Array.isArray(route.query.gtin) ? route.query.gtin[0] : route.query.gtin;

		if (imei && gtin) return { imei: imei, gtin: gtin };

		const gtinOnly = currentTerminal.value?.metadata?.gtin;
		return gtinOnly ? { gtin: gtinOnly } : undefined;
	});

	const tradeInBonus = computed(() => {
		if (steps.value?.wantsToUseInsurance) return gradingData.value?.campaignBonus?.estimatedValue?.withClaim ?? 0;
		if (steps.value?.goToSummary) return gradingData.value?.campaignBonus?.estimatedValue?.withoutClaim ?? 0;
		return 0;
	});

	const estimate = computed(() => {
		if (steps.value?.wantsToUseInsurance) return gradingData.value?.estimatedValue?.withClaim ?? 0;
		if (steps.value?.goToSummary) return gradingData.value?.estimatedValue?.withoutClaim ?? 0;
		return 0;
	});

	const totalEstimate = computed(() => {
		const total = estimate.value + tradeInBonus.value;
		return { total, formattedTotal: formatPrice.oneTime(total) };
	});

	const buildPath = (action: string) =>
		addQueryParams(
			[
				{ path: "path", content: returnPath.value },
				{ path: "swapIn", content: isSwap.value },
			],
			action,
		);

	const nextPath = computed(() => buildPath(nextAction.value));
	const prevPath = computed(() => buildPath(prevAction.value || "/mobiltelefoner/innbytte/bestill/"));

	const completedTradeIn = computed(() => {
		if (isSwap.value) return steps.value?.gradingStepCompleted;
		if (!tradeInItem.value) return false;
		if (steps.value?.goToSummary && selectedTradeInOption.value === TradeInOption.NEW) return true;
		return steps.value?.tradeInCompleted;
	});

	// -- actions --
	const setStepCompleted = async ({ step }: { step: Partial<TradeInSteps> }) => {
		await updateTradeInItem({
			tradeInInfo: { steps: { ...steps.value, ...step } },
		});
	};

	const addTradeInItem = async ({ tradeInInfo }: { tradeInInfo?: TradeInInfo }) => {
		const phoneData = tradeInInfo?.phoneData;
		const [memorySize, memoryUnit] = phoneData?.memorySize?.split(" ") || [];

		const mapped = mapTradeInItem({
			tradeInInfo: {
				...tradeInInfo,
				phoneData: {
					...phoneData,
					memorySize: memorySize || phoneData?.memorySize || "",
					memoryUnit: memoryUnit || phoneData?.memoryUnit || "GB",
				},
			},
		});

		await store.dispatch("shoppingCart/simpleAdd", mapped);
		navigateTo({ path: nextPath.value });
	};

	const updateTradeInItem = async ({ tradeInInfo }: { tradeInInfo: Partial<TradeInInfo> }) => {
		const device = tradeInInfo?.phoneData || meta.value?.phoneData;
		const [memorySize, memoryUnit] = device?.memorySize?.split(" ") || [];

		const merged: TradeInInfo = {
			...meta.value,
			...tradeInInfo,
			phoneData: {
				...device,
				memorySize: memorySize || device?.memorySize || "",
				memoryUnit: memoryUnit || device?.memoryUnit || "GB",
			},
		};
		const mapped = mapTradeInItem({ tradeInInfo: merged });
		await store.dispatch("shoppingCart/simpleAdd", mapped);
	};

	const removeTradeInItem = async (redirect = true) => {
		await store.dispatch("shoppingCart/remove", ShoppingCartItemEnum.TRADE_IN);
		if (route.path.includes("innbytte") && redirect) {
			navigateTo({ path: "/privat/" });
		}
	};

	const cancelOrder = async (reason: string) => {
		try {
			await deviceReturns.cancelOrder(meta.value.deviceOrderId!, reason);
			await updateTradeInItem({
				tradeInInfo: { deviceOrderId: undefined },
			});
		} catch (e) {
			console.error(e);
		}
	};

	return {
		loading,
		deviceOrder,
		hasDeviceOrder,
		gradingAnswers,
		phoneData,
		tradeInItem,
		gradingData,
		insuranceClaimData,
		steps,
		returnPath,
		isSwap,
		tradeInBonus,
		estimate,
		totalEstimate,
		nextPath,
		prevPath,
		completedTradeIn,
		bonusInfoObject,
		selectedTradeInOption,
		isOffset,
		newPhoneData,
		addTradeInItem,
		updateTradeInItem,
		removeTradeInItem,
		setStepCompleted,
		cancelOrder,
	};
};
